import React, {
  useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import _ from 'lodash';
import classes from '../Select/styles.module.scss';
import SelectOptions from '../Select/SelectOptions';
import { ReactComponent as ClearSvg } from '../../../../../assets/icons/close.svg';
import InputField from '../../TextFields/InputField';
import Loader from '../../../Loader/Loader';

const SelectWithSearch = ({
  options = [], searchValue, onChange, onSelectOption, label = '', wrapperClassName = '', height = 50,
  hideClearButton = false, optionWrapperHeight = 209, clearBtnClassName = '', labelBG = '#FFFFFF', error = '',
  optionPathname = 'name', optionKey = 'id', loading = false, zIndex = 11, tableOption = null, errorClassName = '',
}) => {
  const selectRef = useRef();
  const uniqId = useId();

  const [showOptions, showOptionsToggle] = useState(false);
  const [optionsOpacity, setOptionsOpacity] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({});

  const closeMenu = (e) => {
    if (!e.target.closest(`[id="select_container${uniqId}"]`)
      && !e.target.closest(`[id="option_container${uniqId}"]`)) {
      showOptionsToggle(false);
    }
  };

  const setSelectOptionsPosition = () => {
    const selectorRef = document.getElementById(`select_container${uniqId}`);
    const optionsRef = document.getElementById(`option_wrapper${uniqId}`);

    const optionsHeight = optionsRef?.getBoundingClientRect()?.height || 0;

    const { left, top } = selectorRef?.getBoundingClientRect() || { left: 0, top: 0 };

    const topPosition = top + window.scrollY + height;
    const bottomPosition = top + window.scrollY - (optionsHeight + 8);

    const showInTop = window.innerHeight + window.scrollY > topPosition + optionsHeight;

    setOptionsPosition({ left, top: showInTop ? topPosition : bottomPosition });
  };

  useEffect(() => {
    window.addEventListener('click', closeMenu, true);
    window.addEventListener('scroll', setSelectOptionsPosition, true);
    window.addEventListener('resize', setSelectOptionsPosition, true);

    return () => {
      window.removeEventListener('click', closeMenu, true);
      window.removeEventListener('scroll', setSelectOptionsPosition, true);
      window.addEventListener('resize', setSelectOptionsPosition, true);
    };
  }, []);

  useEffect(() => {
    if (showOptions) {
      setSelectOptionsPosition();
      setTimeout(() => setOptionsOpacity(true));
    } else {
      setOptionsOpacity(false);
    }
  }, [showOptions]);

  useLayoutEffect(() => {
    if (searchValue.trim() && !_.isEmpty(options)) {
      showOptionsToggle(true);
    } else {
      showOptionsToggle(false);
    }
  }, [searchValue, options]);

  const onChangeSearch = (value) => {
    onChange(value);
  };

  const selectOption = (option) => {
    onSelectOption(
      optionKey ? option[optionKey] : option,
      optionPathname === 'fullName' ? `${option.firstName} ${option.lastName}` : option[optionPathname],
    );
    setTimeout(() => showOptionsToggle(false));
  };

  const clearOption = () => {
    onSelectOption('');
    onChange('');
    showOptionsToggle(false);
  };

  const wrapperCls = [classes.select_wrapper, wrapperClassName];

  if (error) wrapperCls.push(classes.error);

  const clearCls = [classes.clear_svg, clearBtnClassName];

  const optionsCls = [classes.options_wrapper];

  if (optionsOpacity) optionsCls.push(classes.show);

  const { left, top = 0 } = optionsPosition;

  return (
    <>
      <div
        ref={selectRef}
        id={`select_container${uniqId}`}
        style={{ height }}
        className={wrapperCls.join(' ')}
      >
        <InputField
          label={label}
          type="text"
          value={searchValue}
          onChange={onChangeSearch}
          labelBG={labelBG}
        />

        {loading && <Loader size={12} borderWidth={2} className={classes.select_input_loader} />}

        {/* {search && ( */}
        {/*   <SearchIcon className={classes.search_svg} /> */}
        {/* )} */}

        {searchValue && !hideClearButton && (
          <ClearSvg
            className={clearCls.join(' ')}
            onClick={clearOption}
          />
        )}

        {!!error && typeof error === 'string' && <p className={`${classes.error_text} ${errorClassName}`}>{error}</p>}
      </div>

      {showOptions && createPortal(
        <div
          id={`option_container${uniqId}`}
          style={{
            zIndex, left, top, width: selectRef?.current?.offsetWidth,
          }}
          className={optionsCls.join(' ')}
        >
          <SelectOptions
            items={options}
            hasNextPage={false}
            optionPathname={optionPathname || ''}
            optionWrapperHeight={optionWrapperHeight}
            width={selectRef?.current?.offsetWidth}
            selectOption={selectOption}
            tableOption={tableOption}
            uniqId={uniqId}
          />
        </div>,
        document.getElementById('root') || document.body,
      )}
    </>
  );
};

SelectWithSearch.propTypes = {
  options: PropTypes.array,
  searchValue: PropTypes.string.isRequired,
  optionPathname: PropTypes.string,
  optionKey: PropTypes.string,
  wrapperClassName: PropTypes.string,
  errorClassName: PropTypes.string,
  clearBtnClassName: PropTypes.string,
  label: PropTypes.string,
  height: PropTypes.number,
  loading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSelectOption: PropTypes.func.isRequired,
  error: PropTypes.any,
  labelBG: PropTypes.string,
  zIndex: PropTypes.number,
  tableOption: PropTypes.array,
  hideClearButton: PropTypes.bool,
  optionWrapperHeight: PropTypes.number,
};

export default SelectWithSearch;
